import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../../utils/api';
import useManagementDashboard from '../../hooks/useManagementDashboard';
import ProgramManagementCard from '../../Components/Dashboard/ProgramManagementCard';
import { useToast } from '../../context/ToastContext';

export default function ProgramDetail() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);

  const [selectedBatchId, setSelectedBatchId] = useState('global');
  const [batchSections, setBatchSections] = useState([]);
  const [loadingBatchSections, setLoadingBatchSections] = useState(false);
  const { showToast, showConfirm } = useToast();

  const { data: mgmtData, loading: mgmtLoading } = useManagementDashboard(1);
  
  // Map program name to mgmt key
  const programKey = program?.name?.toLowerCase().includes('praktikum') ? 'praktikum' : 
                     program?.name?.toLowerCase().includes('msib') ? 'msib' : 
                     program?.name?.toLowerCase().includes('mandiri') ? 'mandiri' : null;
  
  const programMgmtData = programKey ? mgmtData?.[programKey] : null;
  const baseColor = programKey === 'praktikum' ? '#0eb9d1' : programKey === 'msib' ? '#6366f1' : '#f59e0b';

  const fetchProgram = async () => {
    try {
      setLoading(true);
      const data = await api.getProgramById({ id });
      setProgram(data);
    } catch (error) {
      console.error('Error fetching program:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProgram();
  }, [id]);



  useEffect(() => {
    if (selectedBatchId && selectedBatchId !== 'global') {
      const fetchBatchSections = async () => {
        try {
          setLoadingBatchSections(true);
          const data = await api.getBatchById({ id: selectedBatchId });
          setBatchSections(data.sections || []);
        } catch (err) {
          console.error(err);
        } finally {
          setLoadingBatchSections(false);
        }
      };
      fetchBatchSections();
    }
  }, [selectedBatchId]);

  const handleEditSection = (section) => {
    setEditingSection(section);
  };

  const handleDeleteSection = async (sectionId, title) => {
    const confirmed = await showConfirm(
      `Yakin ingin menghapus tahapan "${title}"?`,
      'Hapus Tahapan',
      'danger'
    );
    if (!confirmed) return;

    try {
      await api.deleteSections({ sectionId });
      showToast('Tahapan berhasil dihapus.', 'success');
      await fetchProgram();
    } catch (err) {
      showToast('Gagal menghapus tahapan.', 'danger');
    }
  };

  const handleUpdateSection = async (e) => {
    e.preventDefault();
    try {
      await api.updateSectionsProgram({
        sectionId: editingSection.id,
        title: editingSection.title,
        description: editingSection.description,
        sequence: editingSection.sequence,
      });
      showToast('Tahapan berhasil diperbarui!', 'success');
      setEditingSection(null);
      await fetchProgram();
    } catch (err) {
      showToast('Gagal memperbarui tahapan.', 'danger');
    }
  };

  if (loading)
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div
          className="spinner-border text-primary"
          role="status"
          style={{ width: '2rem', height: '2rem' }}
        >
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );

  if (!program)
    return (
      <div className="text-center py-5 text-muted">
        Program tidak ditemukan.
      </div>
    );

  return (
    <div className="container py-4">
      {/* Back Button */}
      <div className="mb-3">
        <button
          type="button"
          className="btn btn-white btn-sm rounded-circle shadow-sm border bg-white text-dark d-inline-flex align-items-center justify-content-center"
          style={{ width: '38px', height: '38px' }}
          onClick={() => navigate(-1)}
        >
          <i className="bi bi-arrow-left fs-5"></i>
        </button>
      </div>

      {/* Header Program */}
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body">
          <h4 className="fw-semibold mb-2">{program.name}</h4>
          <p className="text-muted mb-0">{program.description}</p>
        </div>
      </div>

      {/* Management Insights */}
      {programKey && (
        <div className="row mb-4">
          <div className="col-lg-5">
            <ProgramManagementCard
              title={program.name}
              data={programMgmtData}
              icon={programKey === 'msib' ? 'bi-briefcase' : programKey === 'praktikum' ? 'bi-journal-code' : 'bi-star'}
              baseColor={baseColor}
              loading={mgmtLoading}
            />
          </div>
        </div>
      )}

      {/* Batch Program */}
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-header bg-white border-bottom-0">
          <h5 className="fw-semibold mb-1">Batch Program</h5>
          <small className="text-muted d-block">
            Total batch yang telah berjalan dalam program ini.
            <br />
            <span className="text-dark fw-semibold">Keterangan:</span>
            <br />• <strong>Total Peserta (1–5)</strong> = peserta yang telah
            melewati proses seleksi (
            <em>
              Diproses Seleksi, Lolos FGD/Wawancara, Ditolak, Diterima,
              Mengundurkan Diri
            </em>
            )
            <br />• <strong>Aktif (6)</strong> = peserta yang sedang menjalani
            program <em>internship</em> saat ini.
          </small>
        </div>
        <div className="card-body">
          <div className="row g-3">
            {program.offered.map((batch, i) => (
              <div
                onClick={() => navigate(`/batch/${batch.id}`)}
                style={{ cursor: 'pointer' }}
                key={i}
                className="col-md-6 col-lg-4"
              >
                <div
                  className={`card border-0 shadow-sm h-100 ${
                    batch.statusOffered ? 'border-primary border-2' : ''
                  }`}
                >
                  <div className="card-body">
                    <h6 className="fw-semibold mb-1">
                      Batch {batch.batch}{' '}
                      {batch.statusOffered && (
                        <span className="badge bg-primary ms-2">Aktif</span>
                      )}
                    </h6>
                    <ul className="list-unstyled small mb-0 text-muted">
                      <li>Semua Peserta: {batch.allParticipant}</li>
                      <li>Proses Seleksi (1–5): {batch.totalParticipant}</li>
                      <li>Aktif (6): {batch.activeParticipant}</li>
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tahapan Program */}
      <div className="card shadow-sm border-0 mb-4 rounded-4 overflow-hidden">
        <div className="card-header bg-white border-bottom-0 pt-4 pb-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div>
              <h5 className="mb-0 fw-bold text-primary">
                <i className="bi bi-diagram-3-fill text-primary me-2"></i>Tahapan Program
              </h5>
              <p className="text-muted small mb-0 mt-1">
                Kelola urutan tahapan pelaksanaan untuk program ini secara global atau per batch.
              </p>
            </div>
            {selectedBatchId === 'global' && (
              <button
                className="btn btn-sm btn-primary rounded-pill px-3"
                onClick={() => navigate(`/program/${id}/add-section`)}
              >
                <i className="bi bi-plus-lg me-1"></i> Tambah Tahapan Template
              </button>
            )}
            {selectedBatchId !== 'global' && (
              <Link
                to={`/batch/${selectedBatchId}`}
                className="btn btn-sm btn-outline-primary rounded-pill px-3"
              >
                <i className="bi bi-gear-fill me-1"></i> Kelola Tahapan Batch
              </Link>
            )}
          </div>

          {/* Batch Selector Tabs */}
          <div className="nav nav-pills gap-2 border-bottom-0 pb-1 mt-3 flex-wrap">
            <button
              className={`nav-link rounded-pill px-3 py-1.5 small ${selectedBatchId === 'global' ? 'active btn-primary' : 'bg-light text-secondary border'}`}
              onClick={() => setSelectedBatchId('global')}
            >
              Template Global
            </button>
            {(program.offered || []).map((b) => (
              <button
                key={b.id}
                className={`nav-link rounded-pill px-3 py-1.5 small ${selectedBatchId === b.id ? 'active btn-primary' : 'bg-light text-secondary border'}`}
                onClick={() => setSelectedBatchId(b.id)}
              >
                Batch {b.batch} {b.statusOffered && ' (Aktif)'}
              </button>
            ))}
          </div>
        </div>

        <div className="card-body p-0">
          {selectedBatchId === 'global' ? (
            program.sections.length === 0 ? (
              <div className="text-center py-5">
                <div className="text-muted">
                  <i className="bi bi-info-circle me-2 fs-4"></i>
                  <p className="mb-0 mt-2">Belum ada tahapan global yang ditambahkan untuk program ini.</p>
                  <small className="text-secondary">
                    Tambahkan tahapan agar urutan proses pelaksanaan program lebih terstruktur.
                  </small>
                </div>
              </div>
            ) : (
              <div className="list-group list-group-flush">
                {[...program.sections].sort((a, b) => a.sequence - b.sequence).map((section) => (
                  <div
                    key={section.id}
                    className="list-group-item p-3 d-flex justify-content-between align-items-center hover-bg-light"
                  >
                    <div>
                      <div className="fw-semibold text-dark">
                        {section.sequence}. {section.title}
                      </div>
                      <div
                        className="text-muted small mt-1"
                        style={{ whiteSpace: 'pre-line' }}
                      >
                        {section.description}
                      </div>
                    </div>

                    <div className="ms-3 d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-secondary rounded-circle p-2 d-flex align-items-center justify-content-center"
                        style={{ width: '32px', height: '32px' }}
                        onClick={() => handleEditSection(section)}
                        title="Edit Tahapan"
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger rounded-circle p-2 d-flex align-items-center justify-content-center"
                        style={{ width: '32px', height: '32px' }}
                        onClick={() => handleDeleteSection(section.id, section.title)}
                        title="Hapus Tahapan"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            /* BATCH SECTIONS */
            loadingBatchSections ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary spinner-border-sm me-2" role="status"></div>
                <span className="text-muted small">Memuat tahapan batch...</span>
              </div>
            ) : batchSections.length === 0 ? (
              <div className="text-center py-5">
                <div className="text-muted small">
                  <i className="bi bi-inbox fs-4 d-block mb-2 text-secondary"></i>
                  Belum ada tahapan yang dikonfigurasi untuk batch ini.
                  <br />
                  <Link to={`/batch/${selectedBatchId}`} className="btn btn-sm btn-link mt-2 text-decoration-none">
                    <i className="bi bi-plus-circle me-1"></i>Inisialisasi Tahapan Batch
                  </Link>
                </div>
              </div>
            ) : (
              <div className="list-group list-group-flush">
                {batchSections.map((sec) => (
                  <div
                    key={sec.id}
                    className="list-group-item p-3 d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <div className="fw-semibold text-dark">
                        {sec.sequence ? `${sec.sequence}. ` : ''}{sec.section}
                      </div>
                      <div className="text-muted small mt-1" style={{ whiteSpace: 'pre-line' }}>
                        {sec.sectionDescription}
                      </div>
                      <div className="mt-2 small text-primary d-flex align-items-center gap-1">
                        <i className="bi bi-calendar2-week"></i>
                        <span>{sec.startDate} – {sec.endDate}</span>
                      </div>
                    </div>
                    <div>
                      <span
                        className={`badge rounded-pill text-bg-${
                          sec.statusActive ? 'success' : 'secondary'
                        } px-3 py-1.5`}
                      >
                        {sec.statusActive ? 'Aktif' : 'Nonaktif'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>

      {/* Modal Edit Section */}
      {editingSection && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 rounded-4 shadow">
              <div className="modal-header border-0">
                <h5 className="modal-title">Ubah Tahapan Program</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setEditingSection(null)}
                ></button>
              </div>
              <form onSubmit={handleUpdateSection}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Judul</label>
                    <input
                      type="text"
                      className="form-control rounded-3"
                      value={editingSection.title}
                      onChange={(e) =>
                        setEditingSection({
                          ...editingSection,
                          title: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Deskripsi</label>
                    <textarea
                      className="form-control rounded-3"
                      rows="3"
                      value={editingSection.description}
                      onChange={(e) =>
                        setEditingSection({
                          ...editingSection,
                          description: e.target.value,
                        })
                      }
                      required
                    ></textarea>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Urutan</label>
                    <input
                      type="number"
                      className="form-control rounded-3"
                      value={editingSection.sequence}
                      onChange={(e) =>
                        setEditingSection({
                          ...editingSection,
                          sequence: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer border-0">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => setEditingSection(null)}
                  >
                    Batal
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Simpan Perubahan
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}
